import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { mapboxDirectionApi } from '../../tools/mapbox';
import {
  DirectionValidData,
  isDirectionOk,
  MapboxLine,
  Profile,
  Route,
} from '../../tools/mapbox/apiTypes/direction';

export type Point = {
  latitude: number;
  longitude: number;
};

export type DirectionContextValue = {
  direction?: DirectionValidData;
  updateDirection?: (points: Point[], profile: Profile) => Promise<void>;
  clearDirection?: () => void;
  selectedRoute?: Route | undefined;
  selectedRouteGeometry?: MapboxLine;
  currentProfile?: Profile | undefined;
  directionError?: string;
};

export const DirectionContext = createContext<DirectionContextValue>({});

function getProfileFromURL(): Profile {
  if (typeof window === 'undefined') return Profile.DRIVING;

  const params = new URLSearchParams(window.location?.search);
  const profileParam = params?.get('profile');

  if (profileParam && Object.values(Profile)?.includes(profileParam as Profile))
    return profileParam as Profile;

  return Profile.DRIVING;
}

export function DirectionProvider(props: PropsWithChildren<{}>) {
  const [direction, setDirection] = useState<DirectionValidData>();
  const [directionError, setDirectionError] = useState<string>();
  const [currentProfile, setCurrentProfile] = useState<Profile>(getProfileFromURL);
  const displayedRouteIndex = 0;

  useEffect(() => {
    const handleURLChange = () => {
      const newProfile = getProfileFromURL();
      if (newProfile === currentProfile) return;
      setCurrentProfile(newProfile);
    };

    window.addEventListener('popstate', handleURLChange);
    return () => window.removeEventListener('popstate', handleURLChange);
  }, [currentProfile]);

  const selectedRoute = useMemo(
    () => direction?.routes?.[displayedRouteIndex],
    [direction, displayedRouteIndex]
  );
  const selectedRouteGeometry = useMemo(() => {
    const mapboxLine = selectedRoute?.geometry?.coordinates ?? [];
    return mapboxLine.map(([x, y]) => [y, x]) as MapboxLine;
  }, [selectedRoute]);

  const clearDirection = () => {
    setDirection(undefined);
    setCurrentProfile(undefined);
  };

  const updateDirection = useCallback(async (points: Point[], profile: Profile) => {
    const { direction: newDirection } = await mapboxDirectionApi(profile, points);
    if (isDirectionOk(newDirection)) {
      setDirection(newDirection);
      setCurrentProfile(profile);
      setDirectionError(undefined);

      if (typeof window === 'undefined') return;

      const params = new URLSearchParams(window.location.search);
      params.set('profile', profile);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } else {
      setDirectionError(newDirection.message);
      clearDirection();
    }
  }, []);

  const contextValue = useMemo(() => {
    return {
      direction,
      updateDirection,
      selectedRoute,
      selectedRouteGeometry,
      currentProfile,
      directionError,
      clearDirection,
    };
  }, [
    direction,
    updateDirection,
    selectedRoute,
    selectedRouteGeometry,
    currentProfile,
    directionError,
  ]);

  return (
    <DirectionContext.Provider value={contextValue}>{props.children}</DirectionContext.Provider>
  );
}
